import { Alert, Fab, Snackbar, TablePagination, Typography } from '@mui/material'
import axios from 'axios'
import { useEffect, useMemo, useState } from 'react'
import { ConfirmDialog } from '../components/shared/ConfirmDialog'
import { useDrawerState } from '../components/shared/useDrawerState'
import { usePagination } from '../components/shared/usePagination'
import '../components/styles/ListPage.css'
import './CatalogList.css'
import { CatalogFilters } from './CatalogFilters'
import { CatalogTable } from './CatalogTable'
import { EditCatalogDrawer } from './EditCatalogDrawer'
import type { CatalogRow } from './CatalogList.types'
import { useCatalogData } from './useCatalogData'
import { useCatalogFilters } from './useCatalogFilters'
import { AddCatalogSidebar } from './AddCatalogSidebar'

export default function CatalogList() {
  const { rows, setRows, loading, error: loadError } = useCatalogData()

  const { filters, setFilters, filteredRows, vehicleOptions, contractTypeOptions, setAsideOptions } =
    useCatalogFilters(rows)

  const filteredParentRows = useMemo(() => filteredRows.filter(r => r.parentId === null), [filteredRows])
  const parentLevelContracts = useMemo(() => rows.filter(r => r.parentId === null), [rows])

  const {
    page,
    rowsPerPage,
    pagedItems: pagedParentRows,
    totalCount,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePagination(filteredParentRows, 25)

  const pagedRows = useMemo(() => {
    const parentIds = new Set(pagedParentRows.map(r => r.id))
    return filteredRows.filter(r => (r.parentId === null ? parentIds.has(r.id) : parentIds.has(r.parentId)))
  }, [pagedParentRows, filteredRows])

  const editDrawer = useDrawerState()
  const addDrawer = useDrawerState()
  const [selectedRow, setSelectedRow] = useState<CatalogRow | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  })

  useEffect(() => {
    document.title = 'Contract Catalog | I-Quote'
  }, [])

  const selectedEditRow = useMemo(() => {
    if (!selectedRow) return null
    return rows.find(r => r.id === selectedRow.id) ?? null
  }, [rows, selectedRow])

  const openEditDrawer = (row: CatalogRow) => {
    setSelectedRow(row)
    editDrawer.open()
  }

  const closeEditDrawer = () => {
    editDrawer.close()
    setSelectedRow(null)
  }

  const handleSave = async (patch: Partial<CatalogRow>) => {
    if (!selectedRow) return
    try {
      await axios.put(`/contract/${selectedRow.id}`, patch)
      setRows(prev => prev.map(r => (r.id === selectedRow.id ? { ...r, ...patch } : r)))
      closeEditDrawer()
      setSnackbar({ open: true, message: 'Contract updated.', severity: 'success' })
    } catch (err) {
      console.error('Failed to update contract', err)
      setSnackbar({ open: true, message: 'Failed to update contract. Please try again.', severity: 'error' })
    }
  }

  const handleDelete = async () => {
    if (!selectedRow) return
    try {
      await axios.delete(`/contract/${selectedRow.id}`)
      setRows(prev => prev.filter(r => r.id !== selectedRow.id))
      setDeleteConfirmOpen(false)
      closeEditDrawer()
      setSnackbar({ open: true, message: 'Contract deleted.', severity: 'success' })
    } catch (err) {
      console.error('Failed to delete contract', err)
      setDeleteConfirmOpen(false)
      setSnackbar({ open: true, message: 'Failed to delete contract. Please try again.', severity: 'error' })
    }
  }

  const handleCreate = async (newRow: Partial<CatalogRow>) => {
    try {
      const response = await axios.post('/contract', newRow)
      setRows(prev => [...prev, { ...newRow, id: response.data.id } as CatalogRow])
      addDrawer.close()
      setSnackbar({ open: true, message: 'Contract created.', severity: 'success' })
    } catch (err) {
      console.error('Failed to create contract', err)
      setSnackbar({ open: true, message: 'Failed to create contract. Please try again.', severity: 'error' })
    }
  }

  return (
    <div className="list-page">
      <CatalogFilters
        filters={filters}
        onFiltersChange={setFilters}
        vehicleOptions={vehicleOptions}
        contractTypeOptions={contractTypeOptions}
        setAsideOptions={setAsideOptions}
      />

      <div className="list-table-container">
        <div className="list-table-pagination-header">
          <div className="list-table-pagination-header-left">
            <Typography variant="h6">Contract Catalog</Typography>
          </div>

          <TablePagination
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </div>

        {loading && <Typography className="catalog-status-message">Loading catalog…</Typography>}
        {loadError && <Typography className="catalog-status-message catalog-error-message">{loadError}</Typography>}

        <CatalogTable rows={pagedRows} onRowClick={openEditDrawer} />

        <div className="add-catalog-fab">
          <Fab variant="extended" size="large" color="primary" onClick={() => addDrawer.open()}>
            Add New Catalog
          </Fab>
        </div>

        <div className="catalog-pagination-footer">
          <TablePagination
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </div>
      </div>

      <EditCatalogDrawer
        open={editDrawer.isOpen}
        onClose={closeEditDrawer}
        row={selectedEditRow}
        onSave={handleSave}
        onDelete={() => setDeleteConfirmOpen(true)}
      />

      <AddCatalogSidebar
        open={addDrawer.isOpen}
        parentLevelContracts={parentLevelContracts}
        onCreate={handleCreate}
        onClose={addDrawer.close}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Contract?"
        body="Are you sure you want to delete this contract? This action cannot be undone."
        confirmLabel="DELETE"
        cancelLabel="CANCEL"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirmOpen(false)}
      />

      <Snackbar open={snackbar.open}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  )
}
